// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Stack, Typography } from '@mui/material';

// shared components
import { darkTheme, lightTheme } from '@/components/highcharts/themes';

import { useTheme } from '@emotion/react';
// * Highcharts
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import 'highcharts/modules/no-data-to-display';

const fmt = (v, digits = 2) =>
  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '—';

// api
// * RTK Query hooks
import {
  useGetEnergyConsumptionQuery,
  useGetSeuEnergyConsumptionQuery,
} from '@/api/energy-analysis/calculateEnergySavingsApiSlice';

// shared components
import { parseTimestamp } from '@/components/daterange/datePickerUtils';

// components
// * Hooks
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

const BaseloadDeviationChart = ({
  item,
  energyTreeId,
  account,
  alarm,
  defaultFrom,
  defaultTo,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('alarms');

  console.log('Rendering BaseloadDeviationChart with item:', item);

  // Track series visibility state across item changes
  const [showNormalRange, setShowNormalRange] = useState(false);
  const [showNormalMean, setShowNormalMean] = useState(false);
  const [showConsumption, setShowConsumption] = useState(true);
  const [showAlarmValue, setShowAlarmValue] = useState(false);
  const [showComparisonBands, setShowComparisonBands] = useState(true);
  // const [showBaselineBands, setShowBaselineBands] = useState(false);

  const { from, to } = useUrlDateRange({
    zone: 'Europe/Copenhagen',
    toKey: 'electricityAlarmTo',
    fromKey: 'electricityAlarmFrom',
    autoSetDefaults: false,
  });

  const fallbackFrom = parseTimestamp(defaultFrom);
  const fallbackTo = parseTimestamp(defaultTo);
  const effectiveFrom = from || fallbackFrom;
  const effectiveTo = to || fallbackTo;

  const themeOptions = useMemo(
    () => (theme.palette.mode === 'dark' ? darkTheme : lightTheme),
    [theme.palette.mode],
  );

  // Conditionally use the appropriate query based on item type
  const isNode = item?.type === 'node';
  const isSeu = item?.type === 'seu';

  const {
    data: nodeData,
    isFetching: isFetchingNode,
    isLoading: isLoadingNode,
    isError: isErrorNode,
    error: errorNode,
  } = useGetEnergyConsumptionQuery(
    {
      accountId: account?.accountId,
      energyGroupId: energyTreeId,
      nodeId: item?.nodeId,
      start: effectiveFrom?.toISO(),
      end: effectiveTo?.toISO(),
      aggregate: 'hour',
    },
    {
      skip:
        !account?.accountId ||
        !energyTreeId ||
        !item?.nodeId ||
        !alarm ||
        !effectiveFrom ||
        !effectiveTo ||
        !isNode,
    },
  );

  const {
    data: seuData,
    isFetching: isFetchingSeu,
    isLoading: isLoadingSeu,
    isError: isErrorSeu,
    error: errorSeu,
  } = useGetSeuEnergyConsumptionQuery(
    {
      accountId: account?.accountId,
      energyGroupId: energyTreeId,
      seu: item?.seuType,
      start: effectiveFrom?.toISO(),
      end: effectiveTo?.toISO(),
      aggregate: 'hour',
    },
    {
      skip:
        !account?.accountId ||
        !energyTreeId ||
        !item?.seuType ||
        !alarm ||
        !effectiveFrom ||
        !effectiveTo ||
        !isSeu,
    },
  );

  // Use the appropriate data source
  const data = isNode ? nodeData : seuData;
  const isFetching = isNode ? isFetchingNode : isFetchingSeu;
  const isLoading = isNode ? isLoadingNode : isLoadingSeu;
  const isError = isNode ? isErrorNode : isErrorSeu;
  const error = isNode ? errorNode : errorSeu;

  const lower = typeof item?.lower_limit === 'number' ? item.lower_limit : null;
  const upper = typeof item?.upper_limit === 'number' ? item.upper_limit : null;
  const compared =
    typeof item?.compared_value === 'number' ? item.compared_value : null;
  const i18n = useTranslation().i18n;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const options = useMemo(() => {
    const rows = Array.isArray(data?.EnergyData) ? data?.EnergyData : [];

    // pick the value you want to plot
    const yKey = 'sum'; // or 'sum'

    const consumption = rows
      .map((r) => {
        const x = Date.parse(r?.timestamp); // ms since epoch (or NaN)
        const y = r?.[yKey];
        return Number.isFinite(x) && typeof y === 'number' ? [x, y] : null;
      })
      .filter(Boolean)
      .sort((a, b) => a[0] - b[0]);

    // If no valid points, Highcharts will show "no data"
    const xMin = consumption.length ? consumption[0][0] : null;
    const xMax = consumption.length
      ? consumption[consumption.length - 1][0]
      : null;

    const lower =
      typeof item?.lower_limit === 'number' ? item.lower_limit : null;
    const upper =
      typeof item?.upper_limit === 'number' ? item.upper_limit : null;
    const compared =
      typeof item?.compared_value === 'number' ? item.compared_value : null;

    const limitBand =
      xMin != null && xMax != null && lower != null && upper != null
        ? [
            [xMin, lower, upper],
            [xMax, lower, upper],
          ]
        : [];

    const comparedLine =
      xMin != null && xMax != null && compared != null
        ? [
            [xMin, compared],
            [xMax, compared],
          ]
        : [];

    const meanOfLimits =
      lower != null && upper != null ? (lower + upper) / 2 : null;

    const meanLine =
      xMin != null && xMax != null && meanOfLimits != null
        ? [
            [xMin, meanOfLimits],
            [xMax, meanOfLimits],
          ]
        : [];

    // Convert comparison_periods to plotBands
    const comparisonPlotBands = Array.isArray(item?.comparison_periods)
      ? item.comparison_periods
          .map((period) => {
            const from = Date.parse(period?.start);
            const to = Date.parse(period?.end);
            return Number.isFinite(from) && Number.isFinite(to)
              ? {
                  from,
                  to,
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 165, 0, 0.15)'
                      : 'rgba(255, 140, 0, 0.1)',
                  id: `comparison-${from}`,
                }
              : null;
          })
          .filter(Boolean)
      : [];

    // Convert baseline_periods to plotBands
    // const baselinePlotBands = Array.isArray(item?.baseline_periods)
    //   ? item.baseline_periods
    //       .map((period) => {
    //         const from = Date.parse(period?.start);
    //         const to = Date.parse(period?.end);
    //         return Number.isFinite(from) && Number.isFinite(to)
    //           ? {
    //               from,
    //               to,
    //               color:
    //                 theme.palette.mode === 'dark'
    //                   ? 'rgba(165, 75, 192, 0.15)'
    //                   : 'rgba(165, 75, 192, 0.1)',
    //               id: `baseline-${from}`,
    //             }
    //           : null;
    //       })
    //       .filter(Boolean)
    //   : [];

    return {
      chart: {
        height: 500,
        zoomType: 'x',
        zooming: {
          mouseWheel: { enabled: false },
        },
        backgroundColor: 'transparent',
      },
      title: { text: null },
      credits: { enabled: false },
      time: { useUTC: false },
      xAxis: {
        type: 'datetime',
        crosshair: { width: 1, color: themeOptions.xAxis.crosshairColor },
        labels: {
          style: {
            color: themeOptions.xAxis.labels.style.color,
          },
        },
        plotBands: [
          ...(showComparisonBands ? comparisonPlotBands : []),
          // ...(showBaselineBands ? baselinePlotBands : []),
        ],
      },
      yAxis: {
        crosshair: { width: 1, color: themeOptions.yAxis.crosshairColor },
        gridLineColor: themeOptions.xAxis.gridLineColor,
        lineColor: themeOptions.xAxis.lineColor,
        labels: {
          style: {
            color: themeOptions.xAxis.labels.style.color,
          },
        },
      },
      tooltip: {
        shared: true,
        xDateFormat: '%Y-%m-%d %H:%M',
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        formatter: function () {
          const header = `<b>${dateFormatter.format(new Date(this.x))}</b><br/>`;
          const pointsInfo = this.points
            .map((point) => {
              const name = point.series.name;
              const value = point.y != null ? fmt(point.y) : '—';
              return `<span style="color:${point.color}">\u25CF</span> ${name}: <b>${value} kWh</b>`;
            })
            .join('<br/>');
          return header + pointsInfo;
        },
      },
      legend: {
        itemStyle: { color: themeOptions.legend.itemStyle.color },
      },
      plotOptions: {
        series: { marker: { enabled: false }, turboThreshold: 0 },
        arearange: {
          fillOpacity: 0.2,
          lineWidth: 0,
          enableMouseTracking: false,
          states: { hover: { enabled: false } },
        },
      },
      series: [
        {
          type: 'arearange',
          name: 'Normalområde',
          data: limitBand,
          fillOpacity: 0.1,
          visible: showNormalRange,
          events: {
            legendItemClick: function () {
              setShowNormalRange((prev) => !prev);
              return false;
            },
          },
          // zIndex: 0,
        },
        {
          type: 'line',
          name: 'Normalområde gennemsnit',
          data: meanLine,
          color: theme.palette.mode === 'dark' ? '#D32F2F' : '#B71C1C', // red
          lineWidth: 2,
          enableMouseTracking: false,
          visible: showNormalMean,
          events: {
            legendItemClick: function () {
              setShowNormalMean((prev) => !prev);
              return false;
            },
          },
        },
        {
          type: 'line',
          name: 'Forbrug',
          data: consumption,
          color: theme.palette.mode === 'dark' ? '#3E80D3' : '#143A78',
          visible: showConsumption,
          events: {
            legendItemClick: function () {
              setShowConsumption((prev) => !prev);
              return false;
            },
          },
          // zIndex: 2,
        },
        {
          type: 'line',
          name: 'Alarm værdi',
          data: comparedLine,
          dashStyle: 'Dash',
          color: theme.palette.mode === 'dark' ? '#0BDA51' : '#0AAA40',
          enableMouseTracking: false,
          visible: showAlarmValue,
          events: {
            legendItemClick: function () {
              setShowAlarmValue((prev) => !prev);
              return false;
            },
          },
          // zIndex: 3,
        },
        // Dummy series for comparison period plotbands
        ...(comparisonPlotBands.length > 0
          ? [
              {
                type: 'line',
                name: 'Alarmperiode',
                data: [],
                color:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 165, 0, 0.6)'
                    : 'rgba(255, 140, 0, 0.8)',
                visible: showComparisonBands,
                showInLegend: true,
                enableMouseTracking: false,
                marker: { enabled: false },
                events: {
                  legendItemClick: function () {
                    setShowComparisonBands((prev) => !prev);
                    return false; // Prevent default toggle behavior
                  },
                },
              },
            ]
          : []),
        // Dummy series for baseline period plotbands
        // ...(baselinePlotBands.length > 0
        //   ? [
        //       {
        //         type: 'line',
        //         name: 'Baselineperiode',
        //         data: [],
        //         color:
        //           theme.palette.mode === 'dark'
        //             ? 'rgba(165, 75, 192, 0.6)'
        //             : 'rgba(165, 75, 192, 0.8)',
        //         visible: showBaselineBands,
        //         showInLegend: true,
        //         enableMouseTracking: false,
        //         marker: { enabled: false },
        //         events: {
        //           legendItemClick: function () {
        //             setShowBaselineBands((prev) => !prev);
        //             return false; // Prevent default toggle behavior
        //           },
        //         },
        //       },
        //     ]
        //   : []),
      ],
      lang: {
        noData: isLoading || isFetching ? 'Loading…' : 'No data to display',
      },
    };
  }, [
    data?.EnergyData,
    item,
    showNormalRange,
    showNormalMean,
    showConsumption,
    showAlarmValue,
    showComparisonBands,
    // showBaselineBands,
    themeOptions.xAxis.crosshairColor,
    themeOptions.xAxis.gridLineColor,
    themeOptions.xAxis.lineColor,
    themeOptions.xAxis.labels.style.color,
    themeOptions.yAxis.crosshairColor,
    themeOptions.tooltip.backgroundColor,
    themeOptions.tooltip.borderColor,
    themeOptions.legend.itemStyle.color,
    theme.palette.mode,
    isLoading,
    isFetching,
    dateFormatter,
  ]);

  if (!item) {
    return (
      <Stack spacing={1} sx={{ ml: 2, mt: 4 }}>
        <Typography variant="body2" color="textSecondary">
          {t(
            'type.ElectricitySystem.subtype.BaseloadDeviation.empty_selection',
          )}
        </Typography>
      </Stack>
    );
  }

  if (isError) {
    return (
      <div>
        {t('type.ElectricitySystem.subtype.BaseloadDeviation.failed_to_load')}
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(error, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <Stack spacing={1} sx={{ ml: 2 }}>
      <Stack direction="column" spacing={0} sx={{ mb: 2 }}>
        <Typography variant="caption">
          {t('type.ElectricitySystem.subtype.BaseloadDeviation.alarm_value')}{' '}
          <b>
            {t(
              'type.ElectricitySystem.subtype.BaseloadDeviation.energy_value',
              { value: fmt(compared) },
            )}
          </b>
        </Typography>
        <Typography variant="caption">
          {t('type.ElectricitySystem.subtype.BaseloadDeviation.normal_range')}{' '}
          <b>
            {t(
              'type.ElectricitySystem.subtype.BaseloadDeviation.energy_range',
              { lower: fmt(lower), upper: fmt(upper) },
            )}
          </b>
        </Typography>
      </Stack>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Stack>
  );
};

export default BaseloadDeviationChart;
